import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { patchList } from '@/realtime/patch-list';
import { useRealtime } from '@/realtime/useRealtime';
import { useRealtimePatch } from '@/realtime/useRealtimePatch';
import { useRealtimeResync } from '@/realtime/useRealtimeResync';
import { CheckoutCall, checkoutApi } from '@/services';

export const checkoutKeys = {
  all: ['admin', 'checkout'] as const,
  // Grades sorted before they enter the key: the door screen and a widget can pass the same
  // set in a different order, and an unsorted key would open a second cache entry for it that
  // no pushed `checkout.call` message ever patches — the screen would then look live while
  // going quietly stale.
  calls: (date: string, grades: number[]) =>
    ['admin', 'checkout', 'calls', date, [...grades].sort((a, b) => a - b)] as const,
  candidates: (q: string, date: string) => ['admin', 'checkout', 'candidates', q, date] as const,
  roster: (date: string) => ['admin', 'checkout', 'roster', date] as const,
  /** Prefix over every search this door has run — see `useCancelCallMutation`. */
  candidatesAll: ['admin', 'checkout', 'candidates'] as const,
};

/**
 * Every active student and where they stand, for the whole-school register.
 *
 * Deliberately NOT live-patched itself. The register's own rows only change when a child is
 * called, and `useCheckoutCallsQuery` already carries those events for every grade — so the
 * screen merges the two rather than keeping a second copy of the same truth in a second cache
 * entry that could drift from the first.
 */
export function useCheckoutRosterQuery(date: string) {
  return useQuery({
    queryKey: checkoutKeys.roster(date),
    queryFn: () => checkoutApi.roster(date),
  });
}

/**
 * The waiting list for one date/grade-set. Kept live by patching pushed `checkout.call` events
 * straight into the cache (see useRealtimePatch) rather than refetching, so calling, walking on
 * the way, and collecting all show up on every open screen without a round trip.
 */
export function useCheckoutCallsQuery(date: string, grades: number[], options?: { enabled?: boolean }) {
  const { emit } = useRealtime();
  const queryClient = useQueryClient();
  // `grades` is a fresh array identity every render; key the subscribe effect on this derived
  // primitive instead so it only re-subscribes when the actual grade set changes.
  const gradesKey = grades.join(',');
  // A caller whose grade set is still being resolved passes `enabled: false`. It gates the
  // SUBSCRIPTION as well as the fetch on purpose: a room joined for a placeholder grade set is
  // as wrong as a request made for one, and it would have to be unsubscribed again a render later.
  const enabled = options?.enabled ?? true;

  const query = useQuery({
    queryKey: checkoutKeys.calls(date, grades),
    queryFn: () => checkoutApi.calls(date, grades),
    enabled,
  });

  // The socket's rooms are wider than this list: an admin sits in every grade room from connect,
  // so a screen showing one class still hears the whole school's calls.
  useRealtimePatch<CheckoutCall>(
    'checkout.call',
    checkoutKeys.calls(date, grades),
    c => c.id,
    c => grades.includes(c.gradeLevel)
  );

  useEffect(() => {
    if (!enabled || !grades.length) return;
    emit('checkout:subscribe', { grades });
    return () => emit('checkout:unsubscribe', { grades });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gradesKey stands in for grades
  }, [emit, enabled, gradesKey]);

  // A reconnect gets a new socket, so the room this screen asked for (this exact grade set)
  // is gone until it's re-emitted — the mount effect above only ever ran once. Resubscribing
  // alone isn't enough either: whatever calls/on-ways/collects happened while the socket was
  // down never arrive as a backfill, so the cached list is refetched too, to pick those up.
  useRealtimeResync(() => {
    if (!enabled || !grades.length) return;
    emit('checkout:subscribe', { grades });
    void queryClient.invalidateQueries({ queryKey: checkoutKeys.calls(date, grades) });
  });

  return query;
}

/**
 * Writes a mutation's own response into the exact `(date, grades)` list the caller is viewing,
 * through the same setQueryData + patchList path useRealtimePatch uses for pushes.
 *
 * This is not redundant with the push. A laptop with a dropped or still-reconnecting socket
 * never receives the `checkout.call` message the server emits after the write, and without this
 * the list would sit unchanged after a successful click — with the person at the desk unable to
 * tell whether it worked. `patchList` is an id-keyed upsert, so a later or duplicate push for
 * the same call is a no-op re-render, not a second application.
 *
 * `date`/`grades` are threaded in from the caller rather than discovered via a predicate over
 * every cached checkout list, because `CheckoutCall` carries no `date` field: there is no way to
 * tell, from the record alone, which cached list a freshly *created* call belongs to (the
 * on-way/collected/cancel cases could get away with "patch whichever list already has this id",
 * but a brand-new id is in no list yet). The caller already knows the answer — it's the same
 * (date, grades) its own useCheckoutCallsQuery is under — so all four mutations take it the same
 * way instead of mixing a threaded case with a guessed one.
 */
function patchCallCache(queryClient: QueryClient, date: string, grades: number[], call: CheckoutCall) {
  queryClient.setQueryData<CheckoutCall[]>(checkoutKeys.calls(date, grades), current =>
    patchList(current, call, c => c.id)
  );
}

/**
 * `selfDismissed` marks the call as a child walking home rather than being collected. The roster
 * is invalidated alongside the usual cache patch, because that is the list the door's
 * going-home-alone panel reads: a child who has just been called has to leave it, or reception
 * sends them twice.
 */
export function useCallStudentMutation(date: string, grades: number[]) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ studentId, selfDismissed = false }: { studentId: string; selfDismissed?: boolean }) =>
      checkoutApi.call(studentId, date, selfDismissed),
    onSuccess: call => {
      patchCallCache(queryClient, date, grades, call);
      void queryClient.invalidateQueries({ queryKey: checkoutKeys.roster(date) });
    },
  });
}

export function useMarkOnWayMutation(date: string, grades: number[]) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => checkoutApi.onWay(id),
    onSuccess: call => patchCallCache(queryClient, date, grades, call),
  });
}

export function useMarkCollectedMutation(date: string, grades: number[]) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => checkoutApi.collected(id),
    onSuccess: call => patchCallCache(queryClient, date, grades, call),
  });
}

/**
 * Cancel is also Undo. Withdrawing a call — including one already marked collected by mistake —
 * makes that child callable again, and the search results are where reception has to see that:
 * a row still reading "already collected today", with no Call button, is the dead end this
 * whole affordance exists to open. The waiting list is patched from the response like the other
 * mutations, but a candidate row's availability is the server's judgement over the day's
 * attendance, dismissals and calls, so it is refetched rather than guessed at here.
 */
export function useCancelCallMutation(date: string, grades: number[]) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => checkoutApi.cancel(id),
    onSuccess: call => {
      patchCallCache(queryClient, date, grades, call);
      void queryClient.invalidateQueries({ queryKey: checkoutKeys.candidatesAll });
    },
  });
}

/**
 * Both edges of the day's EDP note. Neither writes into a cache: the note changes whether a child
 * can be called at all, which is the server's judgement over the day's attendance, dismissals,
 * seats and blocks — the same reason `useCancelCallMutation` refetches the candidates rather than
 * guessing at them. Every search this door has run is invalidated (the whole `candidates` prefix,
 * not one query string), plus the register, which shows the same note under its chip.
 */
function invalidateAfterEdpNote(queryClient: QueryClient, date: string) {
  void queryClient.invalidateQueries({ queryKey: checkoutKeys.candidatesAll });
  void queryClient.invalidateQueries({ queryKey: checkoutKeys.roster(date) });
}

export function useExcuseFromEdpMutation(date: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ studentId, reason }: { studentId: string; reason: string }) =>
      checkoutApi.excuseFromEdp(studentId, date, reason),
    onSuccess: () => invalidateAfterEdpNote(queryClient, date),
  });
}

export function useUnexcuseFromEdpMutation(date: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => checkoutApi.unexcuseFromEdp(id),
    onSuccess: () => invalidateAfterEdpNote(queryClient, date),
  });
}

/**
 * Both edges of "a parent is coming today". Like the EDP note beside them, neither guesses at a
 * cache: whether a child belongs on the door's going-home-alone list is the server's judgement
 * over the day, so the register and every search this door has run are refetched instead.
 */
function invalidateAfterPickupNote(queryClient: QueryClient, date: string) {
  void queryClient.invalidateQueries({ queryKey: checkoutKeys.roster(date) });
  void queryClient.invalidateQueries({ queryKey: checkoutKeys.candidatesAll });
}

export function useKeepForPickupMutation(date: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (studentId: string) => checkoutApi.keepForPickup(studentId, date),
    onSuccess: () => invalidateAfterPickupNote(queryClient, date),
  });
}

export function useUndoKeepForPickupMutation(date: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => checkoutApi.undoKeepForPickup(id),
    onSuccess: () => invalidateAfterPickupNote(queryClient, date),
  });
}
